/**
 * EŞİTLEMENİN NEYİ İSTEYECEĞİ — defterden türetilir, TEK KOPYA.
 *
 * Eskiden izlenen araç listesi `TrackingScreen` içinde hesaplanıyordu. P-a
 * ile aynı liste otomatik eşitlemede de gerekti; iki kopya bir gün ayrışır
 * (biri silinmiş satırı saymaya başlar, öteki saymaz) ve kullanıcı ekranda
 * görmediği bir aracın eşitlendiğini ya da gördüğü aracın eşitlenmediğini
 * yaşar. Burada durur, ikisi de buradan okur.
 *
 * LİSTE DEFTERDEN ÇIKAR, ÖNBELLEKTEN DEĞİL: önbellek silinebilir (kota,
 * tarayıcı temizliği), defter kullanıcının verisi.
 */

import { isKnownCurrency } from "@parafon/contracts";
import type { CurrencyCode, InstrumentId, IsoDate, Transaction } from "@parafon/contracts";

/** Merkezin kur kaynağı (TCMB) yalnızca X/TRY yayımlıyor. */
export const KUR_KARSI_PARA: CurrencyCode = "TRY";

export function izlenenAracKimlikleri(rows: readonly Transaction[]): InstrumentId[] {
  const ids = new Set<InstrumentId>();
  for (const t of rows) {
    if (t.deleted) continue;
    if (t.asset.kind === "tracked") ids.add(t.asset.instrumentId);
  }
  return [...ids].sort();
}

/**
 * Kur çiftleri: defterde geçen her para birimi (hareketin, hesabın ve izlenen
 * aracın para birimi) → `[kod, TRY]`.
 *
 * TANINMAYAN KOD İSTENMEZ: "TL" gibi bir kod için merkezde kur yoktur ve
 * olmayacaktır; istemek her açılışta boş bir çağrı demek. O kod raporda zaten
 * `para_birimi_taninmiyor` damgasıyla görünüyor (L şeridi) — sessiz kalmıyor.
 */
export function kurCiftleri(input: {
  readonly transactions: readonly Transaction[];
  readonly accountCurrencies: readonly CurrencyCode[];
  readonly instrumentCurrencies: readonly (CurrencyCode | null)[];
  /**
   * Raporun istenebileceği para birimleri (`RAPOR_PARA_BIRIMLERI`). Defterde
   * hiç USD satırı yokken USD raporu TRY pozisyonları USD/TRY'nin TERSİYLE
   * çeviriyor (motor, `fxPath`) — o kur da çekilmeli. Yoksa rapor "TRY/USD
   * kuru yok" derdi (01.10.2026 kusuru).
   */
  readonly reportCurrencies?: readonly CurrencyCode[];
}): (readonly [CurrencyCode, CurrencyCode])[] {
  const codes = new Set<CurrencyCode>();
  for (const c of input.reportCurrencies ?? []) codes.add(c);
  for (const t of input.transactions) if (!t.deleted) codes.add(t.currency);
  for (const c of input.accountCurrencies) codes.add(c);
  for (const c of input.instrumentCurrencies) if (c !== null) codes.add(c);
  return [...codes]
    .filter((c) => c !== KUR_KARSI_PARA && isKnownCurrency(c))
    .sort()
    .map((c) => [c, KUR_KARSI_PARA] as const);
}

/**
 * Kurun ne kadar geriye isteneceği: defterin en eski işlem günü. Motor her
 * satırı KENDİ gününün kuruyla çeviriyor (H1, M); en eski satırdan önceki kur
 * hiçbir hesaba girmez.
 */
export function enEskiIslemGunu(rows: readonly Transaction[]): IsoDate | null {
  let min: IsoDate | null = null;
  for (const t of rows) {
    if (t.deleted) continue;
    if (min === null || t.tradeDate < min) min = t.tradeDate;
  }
  return min;
}
